import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { SizeCode } from 'entities/SizeCode';
import { KonvaEventObject } from 'konva/lib/Node';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PIXELS_PER_METER } from '../../model/constants/mapScale';
import { CLOSE_THRESHOLD_PX, MIN_VERTEX_COUNT } from '../../model/constants/polygon';
import { getSnapStepPixels } from '../../model/selectors/getSnapStepPixels';
import { warehouseMapConstructorActions } from '../../model/slice/warehouseMapConstructorSlice';
import { Shape, ShapeType, WarehouseMapTool } from '../../model/types';
import { assignNearestSizeCode } from '../helpers/assignNearestSizeCode';
import { Point, snapToGrid } from '../helpers/coordinates';
import { getRelativeCursorPosition } from '../helpers/getRelativeCursorPosition';
import { projectToAxisLock } from '../helpers/projectToAxisLock';
import { Vertex } from '../helpers/polygonVertices';

interface DrawState {
  vertices: Vertex[];
  rubberBand: Vertex | null;
  firstVertexHovered: boolean;
  axisLockGuide: { from: Vertex; to: Vertex } | null;
}

interface Args {
  activeTool: WarehouseMapTool;
  floorId: string;
  availableSizeCodes: SizeCode[] | undefined;
  canvasScale: number;
}

export interface PolygonDrawingApi {
  isDrawing: boolean;
  vertices: Vertex[];
  rubberBand: Vertex | null;
  firstVertexHovered: boolean;
  axisLockGuide: { from: Vertex; to: Vertex } | null;
  onMouseDown: (e: KonvaEventObject<MouseEvent>) => boolean;
  onMouseMove: (e: KonvaEventObject<MouseEvent>) => boolean;
  onDblClick: (e: KonvaEventObject<MouseEvent>) => boolean;
  /** Returns true when the hook consumed the key event. */
  onKeyDown: (e: KeyboardEvent) => boolean;
  cancel: () => void;
}

const IDLE: DrawState = { vertices: [], rubberBand: null, firstVertexHovered: false, axisLockGuide: null };

export const usePolygonDrawing = ({ activeTool, floorId, availableSizeCodes, canvasScale }: Args): PolygonDrawingApi => {
  const dispatch = useAppDispatch();
  const snapStepPixels = useAppSelector(getSnapStepPixels);

  const [drawState, setDrawState] = useState<DrawState>(IDLE);

  // Refs so callbacks always read the latest values without needing to be recreated.
  const drawStateRef = useRef(drawState);
  drawStateRef.current = drawState;
  const snapRef = useRef(snapStepPixels);
  snapRef.current = snapStepPixels;
  const canvasScaleRef = useRef(canvasScale);
  canvasScaleRef.current = canvasScale;
  const floorIdRef = useRef(floorId);
  floorIdRef.current = floorId;
  const availableSizeCodesRef = useRef(availableSizeCodes);
  availableSizeCodesRef.current = availableSizeCodes;
  const activeToolRef = useRef(activeTool);
  activeToolRef.current = activeTool;

  const shiftHeldRef = useRef(false);

  const isPolygonTool = activeTool === WarehouseMapTool.POLYGON || activeTool === WarehouseMapTool.POLYGON_BOX;
  const isDrawing = isPolygonTool && drawState.vertices.length > 0;

  // Cancel drawing when tool or floor changes.
  useEffect(() => {
    setDrawState(IDLE);
  }, [activeTool, floorId]);

  // Track Shift key for axis-lock.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      shiftHeldRef.current = e.shiftKey;
    };
    const onKeyUp = (e: KeyboardEvent): void => {
      shiftHeldRef.current = e.shiftKey;
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  const cancel = useCallback((): void => {
    setDrawState(IDLE);
  }, []);

  const tryCommit = useCallback(
    (vertices: Vertex[]): boolean => {
      if (vertices.length < MIN_VERTEX_COUNT) return false;

      const tool = activeToolRef.current;
      const points = vertices.flatMap((v) => [v.x, v.y]);
      const id = crypto.randomUUID();
      const base = { id, floorId: floorIdRef.current, x: 0, y: 0, scaleX: 1 as const, scaleY: 1 as const, points };

      let shape: Shape;

      if (tool === WarehouseMapTool.POLYGON) {
        shape = { ...base, type: ShapeType.POLYGON, fill: 'transparent', stroke: '#788593', strokeWidth: 4 };
      } else {
        const draft: Shape = { ...base, type: ShapeType.POLYGON_BOX, fill: '#F3F6F9', stroke: '#788593', strokeWidth: 1, label: 'Label' };
        shape = assignNearestSizeCode(draft, availableSizeCodesRef.current, PIXELS_PER_METER);
      }

      dispatch(warehouseMapConstructorActions.appendShape(shape));
      setDrawState(IDLE);
      dispatch(warehouseMapConstructorActions.changeTool(WarehouseMapTool.POINTER));

      return true;
    },
    [dispatch],
  );

  const resolveVertex = (raw: Point, prevVertex: Vertex | undefined): Vertex => {
    const snap = snapRef.current;

    if (shiftHeldRef.current && prevVertex) {
      return snapToGrid(projectToAxisLock(prevVertex, raw), snap);
    }

    return snapToGrid(raw, snap);
  };

  const onMouseDown = useCallback(
    (e: KonvaEventObject<MouseEvent>): boolean => {
      const tool = activeToolRef.current;

      if (tool !== WarehouseMapTool.POLYGON && tool !== WarehouseMapTool.POLYGON_BOX) return false;
      if (e.evt.button !== 0) return false;

      const stage = e.target.getStage();
      const raw = getRelativeCursorPosition(stage);

      if (!raw) return true; // still consume to prevent useMouseArea from running

      const { vertices } = drawStateRef.current;
      const prevVertex = vertices[vertices.length - 1];
      const cursor = resolveVertex(raw, prevVertex);

      // Close trigger: hover over first vertex with enough vertices placed.
      if (vertices.length >= MIN_VERTEX_COUNT) {
        const first = vertices[0];
        const threshold = CLOSE_THRESHOLD_PX / canvasScaleRef.current;

        if (Math.hypot(cursor.x - first.x, cursor.y - first.y) <= threshold) {
          tryCommit(vertices);
          return true;
        }
      }

      setDrawState((prev) => ({ ...prev, vertices: [...prev.vertices, cursor], rubberBand: cursor }));

      return true;
    },
    [tryCommit],
  );

  const onMouseMove = useCallback((e: KonvaEventObject<MouseEvent>): boolean => {
    const tool = activeToolRef.current;

    if (tool !== WarehouseMapTool.POLYGON && tool !== WarehouseMapTool.POLYGON_BOX) return false;

    const { vertices } = drawStateRef.current;

    if (!vertices.length) return false; // not drawing yet; let useMouseArea update cursor ref

    const stage = e.target.getStage();
    const raw = getRelativeCursorPosition(stage);

    if (!raw) return true;

    const lastVertex = vertices[vertices.length - 1];
    const first = vertices[0];

    let rubberBand: Vertex;
    let axisLockGuide: DrawState['axisLockGuide'] = null;

    if (shiftHeldRef.current) {
      const projected = projectToAxisLock(lastVertex, raw);
      rubberBand = projected;
      axisLockGuide = { from: lastVertex, to: projected };
    } else {
      rubberBand = raw;
    }

    const threshold = CLOSE_THRESHOLD_PX / canvasScaleRef.current;
    const firstVertexHovered = vertices.length >= MIN_VERTEX_COUNT && Math.hypot(raw.x - first.x, raw.y - first.y) <= threshold;

    setDrawState((prev) => ({ ...prev, rubberBand, firstVertexHovered, axisLockGuide }));

    return false; // let useMouseArea still update lastCanvasCursorRef
  }, []);

  const onDblClick = useCallback(
    (e: KonvaEventObject<MouseEvent>): boolean => {
      const tool = activeToolRef.current;

      if (tool !== WarehouseMapTool.POLYGON && tool !== WarehouseMapTool.POLYGON_BOX) return false;

      const { vertices } = drawStateRef.current;

      if (vertices.length < MIN_VERTEX_COUNT) return false;

      // The second click of the double-click already placed a vertex via onMouseDown.
      // Commit with the current vertices (which now include that last click).
      tryCommit(vertices);

      return true;
    },
    [tryCommit],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent): boolean => {
      const tool = activeToolRef.current;

      if (tool !== WarehouseMapTool.POLYGON && tool !== WarehouseMapTool.POLYGON_BOX) return false;

      const { vertices } = drawStateRef.current;
      const drawing = vertices.length > 0;

      if (e.key === 'Enter') {
        if (vertices.length >= MIN_VERTEX_COUNT) {
          tryCommit(vertices);
          return true;
        }
        return false;
      }

      if (e.key === 'Escape' && drawing) {
        cancel();
        return true;
      }

      if ((e.key === 'Backspace' || e.key === 'Delete') && drawing) {
        setDrawState((prev) => {
          const next = prev.vertices.slice(0, -1);
          return { ...prev, vertices: next, rubberBand: next.length ? prev.rubberBand : null };
        });
        return true;
      }

      return false;
    },
    [tryCommit, cancel],
  );

  return {
    isDrawing,
    vertices: drawState.vertices,
    rubberBand: drawState.rubberBand,
    firstVertexHovered: drawState.firstVertexHovered,
    axisLockGuide: drawState.axisLockGuide,
    onMouseDown,
    onMouseMove,
    onDblClick,
    onKeyDown,
    cancel,
  };
};
